




















import {
  computed,
  defineComponent,
  reactive,
  Ref,
  ref,
  watch,
} from '@vue/composition-api';
import { useQuery } from '@vue/apollo-composable';
import Color from 'color';
import {
  NOW_START_YEAR,
  NOW,
  toCommaFormat,
  sortByAmount,
} from '@/utils';
import type { Stats } from '@/utils';

import CommonHubSelector from '@/components/CommonHubSelector.vue';
import Treemap from '@/components/Vivat/Chart/Treemap.vue';

import useAuth from '@/useAuth';

const TreemapArea = defineComponent({
  components: {
    Treemap,
    CommonHubSelector,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    query: {
      default: undefined,
    },
    result: {
      type: Function,
      default: undefined,
    },
  },
  setup(props) {
    const { me } = useAuth();
    const filter = reactive({
      hub: null,
      monthRange: {
        from: NOW_START_YEAR,
        to: NOW,
      },
    });
    const { result, loading } = useQuery(
      props.query as any,
      computed(() => {
        const vars = {
          timeframe: filter.monthRange,
          hubId: undefined,
        };

        if (filter.hub && me.hub.hubType === 'MAIN') {
          vars.hubId = (filter.hub as any)?.id;
        } else if (me.hub.hubType !== 'MAIN') {
          vars.hubId = me.hub.id as any;
        }

        return vars;
      }),
      () => ({
        fetchPolicy: 'network-only',
        enabled: props.query !== undefined && props.result !== undefined,
      }),
    );
    const getDataArray = computed(() => {
      if (loading.value === false && result.value && props.result !== undefined) {
        return props.result(result.value);
      }
      return ([{
        type: 'type',
        amount: 0,
      }]);
    });

    const labels: Ref<string[]> = ref([]);
    const dataArray: Ref<number[]> = ref([]);
    const total: Ref<number> = ref(0);
    watch(() => getDataArray.value, (newVal, oldVal) => {
      if (newVal !== oldVal) {
        const map = (record: Stats) => {
          labels.value.push(record.type);
          dataArray.value.push(record.amount);
          total.value += record.amount;
        };

        labels.value = [];
        dataArray.value = [];
        total.value = 0;
        const arr = [...newVal as Stats[]];
        arr.sort(sortByAmount).forEach(map);
      }
    });

    const data = computed(() => ({
      datasets: [{
        tree: dataArray.value,
        spacing: 2,
        borderRadius: 2,
        backgroundColor: (ctx: any) => {
          const index = ctx.dataIndex;
          const color = Color(getComputedStyle(document.documentElement).getPropertyValue('--color-primary-500'));
          const alpha = ((ctx.dataset.data.length - index) / ctx.dataset.data.length) * 100;
          return color.alpha(alpha).string();
        },
        labels: {
          display: true,
          color: 'white',
          font: [{ size: 18, weight: 'bold' }],
          name: labels.value,
          formatter: (ctx: any) => {
            const index = ctx.dataIndex;
            if (index > 2) return ['', ''];
            const value = ctx.dataset.tree[index] || 0;
            const name = labels.value[index] || '';
            return [name, `${toCommaFormat((value * 100) / total.value)}%`];
          },
        },
      }],
    }));

    return {
      me,
      data,
      labels,
      filter,
    };
  },
});

export default TreemapArea;
